// Radius helpers for the unit forms' geofence radius (meters around the unit's
// coordinates). Pure — no React, no i18n. The slider runs on a logarithmic
// scale so 10 m and 500 km share one track without the low end collapsing.

export const RADIUS_MIN = 10;
export const RADIUS_MAX = 500_000;
export const RADIUS_DEFAULT = 100;
/** Slider resolution — positions 0…SLIDER_STEPS map onto RADIUS_MIN…RADIUS_MAX. */
export const SLIDER_STEPS = 1000;

const LOG_SPAN = Math.log10(RADIUS_MAX / RADIUS_MIN);

/** "lat,lng" / "lat, lng" — the data-source importer's `geoCoordinatesRegex`
 *  shape (no leading `+`, no bare `.5` / `24.`) with two deliberate differences:
 *  an optional leading minus on each half, and the whitespace after the comma
 *  is optional. A separate regex by design: the importer's stays positive-only
 *  and space-required by contract. */
export const signedGeoCoordinatesRegex = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;

/** True for an in-range "lat, lng" pair (|lat| ≤ 90, |lng| ≤ 180) — the only
 *  coordinates a radius can be set on, and what the unit forms accept on save. */
export const isCoords = (text: string): boolean => {
  const trimmed = text.trim();
  if (!signedGeoCoordinatesRegex.test(trimmed)) return false;
  const [lat, lng] = trimmed.split(',').map(Number);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
};

/** Integer within [RADIUS_MIN, RADIUS_MAX]; non-finite input falls to the minimum. */
export const clampRadius = (n: number): number =>
  Number.isFinite(n) ? Math.min(RADIUS_MAX, Math.max(RADIUS_MIN, Math.round(n))) : RADIUS_MIN;

/** Meters → slider position (0…SLIDER_STEPS) on the log scale. */
export const radiusToSlider = (meters: number): number =>
  Math.round((Math.log10(clampRadius(meters) / RADIUS_MIN) / LOG_SPAN) * SLIDER_STEPS);

// Snap the raw log value to two significant digits above 100 m so dragging
// lands on clean numbers (860, 1 500, 120 000) rather than 863 / 1 534.
const snap = (meters: number): number => {
  if (meters < 100) return Math.round(meters);
  const magnitude = 10 ** (Math.floor(Math.log10(meters)) - 1);
  return Math.round(meters / magnitude) * magnitude;
};

/** Slider position (0…SLIDER_STEPS) → meters, snapped and clamped. */
export const sliderToRadius = (position: number): number => {
  const ratio = Math.min(SLIDER_STEPS, Math.max(0, position)) / SLIDER_STEPS;
  return clampRadius(snap(RADIUS_MIN * 10 ** (ratio * LOG_SPAN)));
};

export type RadiusUnit = 'm' | 'km';

/** Display value: meters below 1 km, otherwise km with at most two decimals. */
export const formatRadius = (meters: number): { value: string; unit: RadiusUnit } =>
  meters < 1000
    ? { value: String(Math.round(meters)), unit: 'm' }
    : { value: String(Math.round(meters / 10) / 100), unit: 'km' };
